/**
 * Google Analytics 4
 *
 * Loads gtag.js lazily, and only when both are true:
 *   1. `VITE_GA_MEASUREMENT_ID` is set (off in dev, tests, and forks), and
 *   2. the visitor has granted consent (see `./consent`).
 *
 * Until then nothing is fetched and no events are queued or sent. Everything
 * here is fire-and-forget: if the script is blocked or consent is absent,
 * every function becomes a no-op rather than throwing.
 */

import { getConsentSnapshot } from './consent'

type GtagArgs =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['event', string, Record<string, unknown>?]
  | ['set', Record<string, unknown>]

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: GtagArgs) => void
  }
}

/** GA4 measurement id, e.g. "G-XXXXXXXXXX". Empty when analytics is off. */
export const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? ''

/**
 * Whether this build has a measurement id. Configured but unconsented is the
 * state that makes the consent prompt worth showing.
 */
export function isAnalyticsConfigured(): boolean {
  return typeof window !== 'undefined' && MEASUREMENT_ID.length > 0
}

/** Whether analytics may actually run: configured *and* consented. */
export function isAnalyticsEnabled(): boolean {
  return isAnalyticsConfigured() && getConsentSnapshot() === 'granted'
}

let initialized = false

/**
 * Inject gtag.js and configure the property. Safe to call more than once.
 *
 * `send_page_view` is disabled because this is a SPA — page views are sent
 * explicitly on every route change (see `usePageTracking`).
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled() || initialized) return
  initialized = true

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // gtag.js reads the raw `arguments` object back off dataLayer — pushing a
    // rest array instead silently breaks the command queue.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments)
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`
  document.head.appendChild(script)

  window.gtag('js', new Date())
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  })
}

/**
 * Apply a consent decision to an already-loaded gtag.js.
 *
 * Once the script is on the page it can't be unloaded, so withdrawal sets
 * GA's documented per-property opt-out flag and clears the `_ga` cookies it
 * set. Granting consent clears the flag again.
 */
export function applyConsentToAnalytics(consented: boolean): void {
  if (!isAnalyticsConfigured()) return

  // GA reads this global on every hit: https://developers.google.com/analytics/devguides/collection/ga4/disable-analytics
  ;(window as unknown as Record<string, boolean>)[`ga-disable-${MEASUREMENT_ID}`] = !consented

  if (!consented) {
    clearAnalyticsCookies()
  }
}

/** Expire the `_ga` / `_ga_<id>` cookies on this host and its parent domain. */
function clearAnalyticsCookies(): void {
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0] ?? '')
    .filter((name) => name.startsWith('_ga'))

  const { hostname } = window.location
  const parentDomain = hostname.split('.').slice(-2).join('.')
  const domains = ['', hostname, `.${hostname}`, `.${parentDomain}`]

  for (const name of names) {
    for (const domain of domains) {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
        (domain ? `; domain=${domain}` : '')
    }
  }
}

/** Send a SPA page view for the given path. */
export function trackPageView(path: string, title?: string): void {
  if (!isAnalyticsEnabled() || !window.gtag) return

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
  })
}

/**
 * Send a custom event.
 *
 * Params must be flat primitives — GA4 drops nested objects. Never pass file
 * contents, file names, or anything else derived from a user's settings.json;
 * counts and enum-ish strings only.
 */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!isAnalyticsEnabled() || !window.gtag) return
  window.gtag('event', name, params ?? {})
}

// =============================================================================
// Event helpers
//
// Named wrappers keep event names and param shapes consistent across the app.
// =============================================================================

export const analyticsEvents = {
  filesAdded: (source: 'upload' | 'paste', count: number, totalFiles: number) =>
    trackEvent('files_added', { source, count, total_files: totalFiles }),

  fileRemoved: (totalFiles: number) => trackEvent('file_removed', { total_files: totalFiles }),

  filesReordered: (totalFiles: number) => trackEvent('files_reordered', { total_files: totalFiles }),

  duplicateDetected: () => trackEvent('duplicate_detected'),

  ruleExcluded: () => trackEvent('rule_excluded'),

  ruleIncluded: () => trackEvent('rule_included'),

  ruleRecategorized: (category: string) => trackEvent('rule_recategorized', { category }),

  conflictResolved: () => trackEvent('conflict_resolved'),

  undo: (source: 'keyboard' | 'toast') => trackEvent('undo', { source }),

  export: (
    method: 'copy' | 'download' | 'save_as',
    stats: { sourceFileCount: number; totalPermissions: number; conflictCount: number }
  ) =>
    trackEvent('export_settings', {
      method,
      source_files: stats.sourceFileCount,
      permissions: stats.totalPermissions,
      conflicts: stats.conflictCount,
    }),

  copyFallbackShown: () => trackEvent('copy_fallback_shown'),

  clearAll: (totalFiles: number) => trackEvent('clear_all', { total_files: totalFiles }),

  themeToggled: (theme: string) => trackEvent('theme_toggled', { theme }),

  feedbackBannerShown: () => trackEvent('feedback_banner_shown'),

  feedbackBannerDismissed: () => trackEvent('feedback_banner_dismissed'),

  feedbackModalOpened: (source: 'banner' | 'footer') =>
    trackEvent('feedback_modal_opened', { source }),

  feedbackSubmitted: () => trackEvent('feedback_submitted'),

  outboundClick: (destination: string) => trackEvent('outbound_click', { destination }),
}
